import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Grid,
    Input,
    Text,
    useColorModeValue
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import Loading from "components/Loading/Loading";
import { ROUTES } from "constants/routes-path";
import { useCustomToast } from "components/CustomToast/CustomToast";
import SearchSelect from "components/SearchSelect/SearchSelect";
import SwitchInput from "components/SwitchInput/SwitchInput";
import { agentsService } from "services/agentsService";
import { aitBlockService } from "services/aitBlockService";
import { equipmentsService } from "services/equipmentsService";
import { checkErrors, hasError } from "utils/forms-utils";
import ListPageCardHeader from "components/Card/ListPageCardHeader";

function AitBlockNew({ data }) {
    const { t } = useTranslation();
    const { id } = useParams();
    const textColor = useColorModeValue("gray.700", "white");
    const [formData, setFormData] = useState(undefined);
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const [inputError, setInputError] = useState({});
    const highestIntSql = 2147483647;

    const ids = useMemo(() => ({
        id: "ait-id",
        applicability: "applicability",
        prefix: "ait-prefix",
        initialNumber: "ait-initialNumber",
        endNumber: "ait-endNumber",
        validatorDigit: "ait-validatorDigit",
        agent: "ait-agent",
        equipment: "ait-equipment",
        creationDate: "ait-creationDate"
    }), []);

    const isFormInvalid = formData && inputError ? hasError({ formData, inputError }) : true;

    const initFormData = (data) => ({
        [ids.applicability]: {
            value: data?.aplicabilidade || "",
            validations: [{ required: true }],
        },
        [ids.prefix]: {
            value: data?.prefixo || "",
            validations: [{ required: true }],
        },
        [ids.initialNumber]: {
            value: data?.numero_inicial || "",
            validations: [{ required: true }],
        },
        [ids.endNumber]: {
            value: data?.numero_final || "",
            validations: [{ required: true }],
        },
        [ids.validatorDigit]: {
            value: data?.digito_validador ?? true,
            validations: [],
        },
        [ids.agent]: {
            value: data?.id_agente || null,
            validations: [],
        },
        [ids.equipment]: {
            value: data?.id_equipamento || null,
            validations: [],
        },
        [ids.creationDate]: {
            value: data?.data_criacao || "",
            validations: [],
        },
    });

    const fetchData = async () => {
        try {
            if (id) {
                const aitBlockDetails = await aitBlockService.find(+id);
                setFormData(initFormData(aitBlockDetails));
            } else {
                setFormData(initFormData());
            }
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    }

    useEffect(() => {
        fetchData();
    }, [id]);

    const handleChange = (e) => {
        const { id, value } = e.target;

        let newValue = value;

        if (id === ids.agent) {
            newValue = agentData.find((agent) => agent.id === parseInt(value)) || "";
        }

        if (id === ids.equipment) {
            newValue = equipmentData.find((eq) => eq.id === parseInt(value)) || "";
        }

        if (id === ids.initialNumber || id === ids.endNumber) {
            newValue = Math.min(highestIntSql, value);
        }

        const newFormData = {
            ...formData,
            [id]: {
                ...formData[id],
                value: newValue,
            },
        };

        setFormData(newFormData);
        setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
    };

    const formatEquipmentData = (equipmentData) => {
        if (equipmentData?.data_verificacao) {
            equipmentData.data_verificacao = equipmentData.data_verificacao.split('/').reverse().join('-');
        }
        return equipmentData;
    }

    const toDTO = () => ({
        aplicabilidade: formData[ids.applicability].value,
        prefixo: formData[ids.prefix].value,
        numero_inicial: formData[ids.initialNumber].value,
        numero_final: formData[ids.endNumber].value,
        digito_validador: formData[ids.validatorDigit].value,
        id_agente: formData[ids.agent].value ? formData[ids.agent].value : null,
        id_equipamento: formData[ids.equipment].value ? formatEquipmentData(formData[ids.equipment].value) : null,
        dataCriacao: new Date()
    });

    const handleSubmit = async () => {
        if (isFormInvalid) return;

        try {
            const dataToSave = toDTO();

            id ? await aitBlockService.update(id, dataToSave) : await aitBlockService.create(dataToSave)

            navigate(ROUTES.aitBlocksPath);

            showToast({
                title: t(`settings.ait-block.messages.success.${id ? 'edit' : 'create'}`),
                status: "success",
            });
        } catch (error) {
            showToast({ title: t(error), status: "error" });
        }
    };

    const [equipmentData, setEquipmentData] = useState([]);
    const [agentData, setAgentData] = useState([]);
    const [applicabilityData, setApplicabilityData] = useState([]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const resAgent = await agentsService.list({ pageSize: 999 });
                setAgentData(resAgent.content);
            } catch (error) {
                console.error(t("system.error.api.loading-data"), error);
            }
        };

        void loadData();
    }, []);

    useEffect(() => {
        const loadData = async () => {
            try {
                await fetchParametersEquipments({});
            } catch (error) {
                console.error(t("system.error.api.loading-data"), error);
            }
        };

        void loadData();
    }, []);

    useEffect(() => {
        const loadData = async () => {
            try {
                await fetchParametersApplicabilities({});
            } catch (error) {
                console.error(t("system.error.api.loading-data"), error);
            }
        };

        void loadData();
    }, []);

    const fetchParametersEquipments = async (params) => {
        const resEquipment = await equipmentsService.list({ pageSize: 999 });

        if (resEquipment && resEquipment.content) {
            const resEquipmentFiltered = resEquipment.content
                .map((obj) => {
                    const { _links, ...filtered } = obj;
                    return filtered;
                })
                .sort((a, b) => a.id - b.id);
            setEquipmentData(resEquipmentFiltered);
            return resEquipmentFiltered;
        } else {
            console.error(t("system.error.api.unexpected"), res);
            setEquipmentData([]);
            return [];
        }
    };

    const fetchParametersApplicabilities = async (params) => {
        const resApplicability = await aitBlockService.getApplicabilities(JSON.stringify({}));
        if (resApplicability) {
            const resApplicabilityFiltered = resApplicability
                .map((obj) => {
                    const { _links, ...filtered } = obj;
                    return filtered;
                })
                .sort((a, b) => a.id - b.id);
            setApplicabilityData(resApplicabilityFiltered);
            return resApplicabilityFiltered;
        } else {
            console.error(t("system.error.api.unexpected"), res);
            setApplicabilityData([]);
            return [];
        }
    };

    return (
        <Flex direction="column" minH="89vh" align="center" pt={{ sm: "120px", md: "90px" }}>
            <Card>
                <ListPageCardHeader
                    title={t(`settings.ait-block.form.title${data ? '-edit' : ''}`)}
                    subtitle={t(`settings.ait-block.form.subtitle${data ? '-edit' : ''}`)}
                />
                <CardBody px="22px">
                    {formData && equipmentData && agentData && applicabilityData ? (
                        <Flex w="100%" gap="40px" direction="column">
                            <Grid
                                templateColumns={{ sm: "1fr", md: "repeat(2, 1fr)" }}
                                templateRows={{ md: "repeat(2, 1fr)" }}
                                gap="30px"
                            >
                                <FormControl isRequired isInvalid={inputError[ids.applicability]}>
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t('settings.ait-block.form.fields.applicability.label')}
                                    </FormLabel>
                                    <SearchSelect
                                        required
                                        id={ids.applicability}
                                        value={formData[ids.applicability].value}
                                        isDisabled={!applicabilityData.length}
                                        items={applicabilityData}
                                        valueField="nome"
                                        displayField="descricao"
                                        onChange={handleChange}
                                        placeholder={t('settings.ait-block.form.fields.applicability.placeholder')}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.applicability])}
                                    </FormErrorMessage>
                                </FormControl>
                                <FormControl isRequired isInvalid={inputError[ids.prefix]}>
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t('settings.ait-block.form.fields.prefix.label')}
                                    </FormLabel>
                                    <Input
                                        variant="main"
                                        id={ids.prefix}
                                        value={formData[ids.prefix].value}
                                        onChange={handleChange}
                                        placeholder={t('settings.ait-block.form.fields.prefix.placeholder')}
                                        fontSize="sm"
                                        maxLength={255}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.prefix])}
                                    </FormErrorMessage>
                                </FormControl>
                                <FormControl isRequired isInvalid={inputError[ids.initialNumber]}>
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t('settings.ait-block.form.fields.initial-number.label')}
                                    </FormLabel>
                                    <Input
                                        variant="main"
                                        id={ids.initialNumber}
                                        type="number"
                                        value={formData[ids.initialNumber].value}
                                        onChange={handleChange}
                                        placeholder={t('settings.ait-block.form.fields.initial-number.placeholder')}
                                        fontSize="sm"
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.initialNumber])}
                                    </FormErrorMessage>
                                </FormControl>
                                <FormControl isRequired isInvalid={inputError[ids.endNumber]}>
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t('settings.ait-block.form.fields.end-number.label')}
                                    </FormLabel>
                                    <Input
                                        variant="main"
                                        id={ids.endNumber}
                                        type="number"
                                        value={formData[ids.endNumber].value}
                                        onChange={handleChange}
                                        placeholder={t('settings.ait-block.form.fields.end-number.placeholder')}
                                        fontSize="sm"
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.endNumber])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={inputError[ids.agent]}>
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t('settings.ait-block.form.fields.agent.label')}
                                    </FormLabel>
                                    <SearchSelect
                                        id={ids.agent}
                                        value={formData[ids.agent].value?.id || ""}
                                        isDisabled={!agentData.length}
                                        items={agentData}
                                        valueField="id"
                                        displayField={["codigo", "nome"]}
                                        placeholder={t('settings.ait-block.form.fields.agent.placeholder')}
                                        onChange={handleChange}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.agent])}
                                    </FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={inputError[ids.equipment]}>
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t('settings.ait-block.form.fields.equipment.label')}
                                    </FormLabel>
                                    <SearchSelect
                                        id={ids.equipment}
                                        value={formData[ids.equipment].value?.id || ""}
                                        isDisabled={!equipmentData.length}
                                        items={equipmentData}
                                        valueField="id"
                                        displayField="modelo"
                                        placeholder={t('settings.ait-block.form.fields.equipment.placeholder')}
                                        onChange={handleChange}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.equipment])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={inputError[ids.validatorDigit]}>
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t('settings.ait-block.form.fields.validator-digit.label')}
                                    </FormLabel>
                                    <SwitchInput
                                        name={ids.validatorDigit}
                                        defaultChecked={formData[ids.validatorDigit].value}
                                        onChange={(e) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                [e.name]: {
                                                    ...prev[e.name],
                                                    value: e.value,
                                                },
                                            }));
                                        }}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.validatorDigit])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Grid>

                            <Flex w="100%" justify="space-between">
                                <Button
                                    w="125px"
                                    variant="no-effects"
                                    bg="gray.100"
                                    color="gray.700"
                                    fontSize="sm"
                                    fontWeight="bold"
                                    onClick={() => navigate(ROUTES.aitBlocksPath)}
                                >
                                    {t(`system.actions.${id ? 'cancel' : 'back'}`)}
                                </Button>
                                <Button
                                    w="125px"
                                    variant="dark"
                                    fontSize="sm"
                                    type="submit"
                                    isDisabled={isFormInvalid}
                                    onClick={handleSubmit}
                                    _hover={isFormInvalid ? {} : undefined}
                                >
                                    {t(`system.actions.${id ? 'edit' : 'save'}`)}
                                </Button>
                            </Flex>
                        </Flex>
                    ) : (
                        <Loading h="300px" w="100%" />
                    )}
                </CardBody>
            </Card>
        </Flex>
    );
}

export default AitBlockNew;